import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { Dialog } from './dialog';

// Every dialog component must accept onClose, injected by the provider
interface DialogBaseProps {
  onClose: () => void;
}

interface DialogEntry {
  id: string;
  // Component may be React.lazy — provider wraps each dialog in Suspense
  component: ComponentType<DialogBaseProps>;
  props: Record<string, unknown> | null;
  isOpen: boolean;
}

interface DialogContextValue {
  register: (id: string, component: ComponentType<DialogBaseProps>) => void;
  unregister: (id: string) => void;
  open: (id: string, props: Record<string, unknown>) => void;
  close: (id: string) => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

function useDialogContext(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within a DialogProvider');
  return ctx;
}

function DialogProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<DialogEntry[]>([]);

  const register = useCallback((id: string, component: ComponentType<DialogBaseProps>) => {
    setEntries((prev) => {
      if (prev.some((e) => e.id === id)) return prev;
      return [...prev, { id, component, props: null, isOpen: false }];
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const open = useCallback((id: string, props: Record<string, unknown>) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, props, isOpen: true } : e)));
  }, []);

  const close = useCallback((id: string) => {
    // Phase 1: trigger Radix close animation
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, isOpen: false } : e)));
    // Phase 2: unmount component after animation (matches dialog duration-200)
    setTimeout(() => {
      setEntries((prev) => prev.map((e) => (e.id === id && !e.isOpen ? { ...e, props: null } : e)));
    }, 200);
  }, []);

  const value = useMemo(
    () => ({ register, unregister, open, close }),
    [register, unregister, open, close]
  );

  return (
    <DialogContext.Provider value={value}>
      {children}
      {entries.map((entry) => (
        <DialogOutletItem key={entry.id} entry={entry} close={close} />
      ))}
    </DialogContext.Provider>
  );
}

interface DialogOutletItemProps {
  entry: DialogEntry;
  close: (id: string) => void;
}

function DialogOutletItem({ entry, close }: DialogOutletItemProps) {
  const handleClose = useCallback(() => close(entry.id), [close, entry.id]);
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) close(entry.id);
    },
    [close, entry.id]
  );

  return (
    <Dialog open={entry.isOpen} onOpenChange={handleOpenChange}>
      {/* Suspense handles lazy-loaded dialog components */}
      <Suspense>
        {entry.props !== null && <entry.component {...entry.props} onClose={handleClose} />}
      </Suspense>
    </Dialog>
  );
}

export { DialogProvider, useDialogContext };
export type { DialogBaseProps };
